import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { CheckStandingAuthDetails, isCheckStandingAuthDetails } from "../../models/standingAuthorizationForm";
import { Observable, Subject, combineLatest, takeUntil } from "rxjs";
import { selectViewInContext } from "../../../shared/reducers";
import { Store } from "@ngrx/store";
import { selectIsFromTableOrView, selectStagedStandingAuthForm, selectStandingAuthCheckInfo, selectStandingAuthForm } from "../../reducers";
import { CheckAbbreviatedRecipientTypes, CheckPayeeTypes, checkRecipientDict, distributionReasons, transactionDetailLabels, transactionTypesDict } from "../../../shared/utilities/Constants";
import { StandingAuthCheckInfo } from "../../models/StandingAuthCheckInfo";
import { CodeAndDescription } from "../../../create-request/models/shared/CodeAndDescription";
import { selectIsIra, selectNraIndicator, selectStateCode, selectUseTestTrustDigitalToPdfFlow } from "../../../account-info-header/reducers";
import { StandingAuthReviewPageActions } from "../../actions";
import { selectIsStandingAuthViewPage } from "../../../transaction-view/reducers";

@Component({
  standalone: false,
  selector: 'mm-sa-transaction-details-section-check',
  templateUrl: './sa-transaction-details-section-check.component.html',
  styleUrls: ['./sa-transaction-details-section-check.component.scss']
})
export class SaTransactionDetailsSectionCheckComponent implements OnInit, OnDestroy {
  @Input() isInrFromTable: boolean;
  readonly labels = transactionDetailLabels;
  createRequestStep$: Observable<string>;
  destroy$ = new Subject<void>();
  transactionType: string;
  standingAuthDetails: CheckStandingAuthDetails;
  info: StandingAuthCheckInfo;
  payeeName: string[];
  payeeType: string;
  memo: string;
  deliveryAddress: string[];
  recipient: CheckAbbreviatedRecipientTypes;
  checkReason: string;
  iraAccount: boolean;
  fedTaxPercent: string;
  stateTaxPercent: string;
  distributionReason: string;
  isStandingAuthViewPage: boolean;
  saStateTaxLabel: string = transactionDetailLabels.stateTax;
  isCheckSloa: boolean;
  isQCDCheckSloa: boolean;
  isFromTableOrView: boolean;

  constructor(private store: Store) { }

  ngOnInit(): void {
    this.createRequestStep$ = this.store.select(selectViewInContext);

    this.store.select(selectIsIra).pipe(takeUntil(this.destroy$)).subscribe((isIra) => {
      this.iraAccount = isIra;
    });

    this.store.select(selectIsFromTableOrView).pipe(takeUntil(this.destroy$)).subscribe((isFromTableOrView) => {
      this.isFromTableOrView = isFromTableOrView;  
    });

    combineLatest([
      this.store.select(selectStagedStandingAuthForm),
      this.store.select(selectStandingAuthCheckInfo),
      this.store.select(selectUseTestTrustDigitalToPdfFlow),
      this.store.select(selectStateCode),
      this.store.select(selectNraIndicator)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([form, checkInfo, useTestTrustDigitalToPdfFlow, stateCode, nraIndicator]) => {  this.info = checkInfo;

      if (form.transactionType) {
        this.transactionType = transactionTypesDict[form.transactionType];
        if (form.isSubmitAndCreateBundledSa){
          this.transactionType += ' - bundled';
        }
      }
      this.isCheckSloa = isCheckStandingAuthDetails(form.standingAuthorizationDetails);
      if (this.isCheckSloa) {
        this.standingAuthDetails = form.standingAuthorizationDetails as CheckStandingAuthDetails;
        this.recipient = checkRecipientDict[this.standingAuthDetails.recipient!] as CheckAbbreviatedRecipientTypes;
        this.deliveryAddress = this.standingAuthDetails.recipient === 'firstParty' ? [this.recipient as string].concat(checkInfo.defaultRecipient) : [this.recipient as string].concat(checkInfo.deliveryAddress);
        this.payeeType = this.standingAuthDetails.payeeType === 'thirdParty' ? 'Third party' : 'First party (account holder)';
        this.payeeName = useTestTrustDigitalToPdfFlow ? checkInfo.payee : this.getPayeeName(this.standingAuthDetails, this.standingAuthDetails.modifiedPayeeCheckbox ?? false, checkInfo);        this.checkReason = this.standingAuthDetails.checkReason ?? '';
        this.fedTaxPercent = checkInfo.fedTaxRate ?? '';
        this.stateTaxPercent = this.standingAuthDetails.stateTaxPercent ?? '';
        this.distributionReason = this.standingAuthDetails.distributionReason ?? '';
      }
      this.isQCDCheckSloa = this.isFromTableOrView && this.iraAccount && this.isCheckSloa && this.distributionReason === distributionReasons.qcd;
      if (stateCode && !nraIndicator) {
        this.saStateTaxLabel = `${this.labels.stateTax} (${stateCode})`;
      }
    });

    this.store.select(selectIsStandingAuthViewPage).pipe(takeUntil(this.destroy$)).subscribe(value => this.isStandingAuthViewPage = value);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get displayNickname(): boolean {
    return !!this.standingAuthDetails.authorizationNickname;
  }

  get displayReason(): boolean {
    return !!this.standingAuthDetails.checkReason?.trim();
  }

  get displayMemo(): boolean {
    return !!this.standingAuthDetails.checkMemo?.trim();
  }

  getPayeeName(transactionDetails: CheckStandingAuthDetails, isFirstPartyModified: boolean, checkInfo: StandingAuthCheckInfo): string[] {
    let payees: string[] = [];
    if (this.isStandingAuthViewPage) {
      payees.push(checkInfo.payeeDetails.payeeName1)
      payees.push(checkInfo.payeeDetails.payeeName2)
      payees.push(checkInfo.payeeDetails.payeeName3)
    }
    else if (isFirstPartyModified) {
      payees.push(transactionDetails?.modifiedPayeeName1 ?? '');
      payees.push(transactionDetails?.modifiedPayeeName2 ?? '');
      payees.push(transactionDetails?.modifiedPayeeName3 ?? '');
    }
    else if (transactionDetails.payeeType === 'firstParty') {
      payees = (checkInfo.payee).filter((name: string) => !!name.trim());
    }
    else {
      if (transactionDetails.payeeName) payees.push(transactionDetails.payeeName.toUpperCase());
      if (transactionDetails.additionalPayeeName1) payees.push(transactionDetails.additionalPayeeName1.toUpperCase());
      if (transactionDetails.additionalPayeeName2) payees.push(transactionDetails.additionalPayeeName2.toUpperCase());
    }
    return payees;
  }

  selectReason(code: string): string {
    let filtered = this.info.checkReasonMap.find((item: CodeAndDescription) => item.code === code);
    return filtered?.description ?? '';
  }

  onEditClicked() {
    this.store.dispatch(StandingAuthReviewPageActions.editClicked());
  }
}
